import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth, googleProvider, signInWithPopup, signInWithRedirect, getRedirectResult,
  signInWithEmailAndPassword, createUserWithEmailAndPassword,
  signOut, onAuthStateChanged, updateProfile as fbUpdateProfile,
} from '../services/firebase';
import api from '../services/api';

const AuthContext = createContext(null);
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Safely check for redirect result — in-app browsers (WhatsApp, Instagram) 
    // have restricted sessionStorage which breaks getRedirectResult
    try {
      getRedirectResult(auth).catch(() => {});
    } catch (e) {
      // Silently ignore — in-app browser with restricted storage
      console.log('Auth redirect check skipped (restricted browser)');
    }

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        try {
          // Get token DIRECTLY from the Firebase user object
          // This avoids the race condition in api.js getToken()
          const token = await fbUser.getIdToken(true);

          const { user: dbUser } = await api.register({
            displayName: fbUser.displayName || '',
            email: fbUser.email || '',
            photoURL: fbUser.photoURL || '',
          }, token);

          setUser(dbUser);

          // Track referral if user signed up via referral link
          const refCode = sessionStorage.getItem('vt_referral_code');
          if (refCode && dbUser) {
            try {
              await api.request('POST', '/api/referrals/track-signup', { referralCode: refCode });
              sessionStorage.removeItem('vt_referral_code');
            } catch {}
          }

          // Redeem founding family code if the user arrived via a /founding/:code link
          const foundingCode = sessionStorage.getItem('vt_founding_code');
          if (foundingCode && dbUser && !dbUser.foundingMember?.isFounding) {
            try {
              const { user: updated } = await api.redeemFoundingCode(foundingCode);
              if (updated) setUser(updated);
              sessionStorage.removeItem('vt_founding_code');
            } catch {}
          }
        } catch (err) {
          console.error('Failed to sync user with DB:', err);
          setUser({
            firebaseUid: fbUser.uid,
            email: fbUser.email,
            displayName: fbUser.displayName || fbUser.email?.split('@')[0] || '',
            subscription: { plan: 'roots', status: 'active' },
            trees: [],
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (err) {
      // Ignore user cancellations
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        return null;
      }
      // For popup-blocked, show a helpful message instead of broken redirect
      if (err.code === 'auth/popup-blocked') {
        throw new Error('Popup blocked. Please allow popups for this site, or use email/password login.');
      }
      throw err;
    }
  };

  const loginWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const registerWithEmail = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await fbUpdateProfile(result.user, { displayName });
    }
    return result.user;
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { user: dbUser } = await api.getMe();
      setUser(dbUser);
    } catch (err) {
      console.error('Refresh user failed:', err);
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      const { user: updated } = await api.updateProfile(updates);
      setUser(updated);
      return updated;
    } catch (err) {
      console.error('Update profile failed:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{
      firebaseUser, user, loading,
      loginWithGoogle, loginWithEmail, registerWithEmail,
      logout, refreshUser, updateProfile: updateUserProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
